import moment from "moment/moment";
import { createSelector } from "reselect";
import { getTimeWorkdays } from "~/store/selectors/selector.configOptions";
import { getNonWorkingDayByDate } from "~/store/selectors/selector.daysOff";
import { getAllWeeklyUserCapacityData } from "~/store/selectors/selector.weeklyWorkerCapacity";
import { getCalendarByEventId } from "./selector.calendarEvents";
import { getGhostTasksByDate } from "./selector.ghostTasks";
import { getAllProjects } from "./selector.projects";
import { getRemindersByDate } from "./selector.reminders";
import { getSubTasksByDate } from "./selector.subtasks";
import { getTasksByDate } from "./selector.tasks";
import { getLoggedUser } from "./selector.users";

export const getAllCalendars = (state) => state.calendars;

export const getCalendarById = createSelector(
  getAllCalendars,
  (state, id) => id,
  (calendars, id) => {
    return calendars[id];
  }
);

export const getCalendarMembersByCalendarId = createSelector(getCalendarById, (calendar) => {
  if (calendar) {
    return calendar.members;
  }
  return [];
});

export const getCalendarMembersByEventId = createSelector(getCalendarByEventId, getAllCalendars, (calendar) => {
  if (calendar) {
    return calendar.members;
  }
  return [];
});

export const getActiveCalendars = createSelector(getAllCalendars, getLoggedUser, (calendars, loggedUser) => {
  return Object.values(calendars).filter((calendar) => {
    return calendar?.members?.includes(loggedUser?.id) && !calendar.is_trashed;
  });
});

export const getInternalCalendars = createSelector(getActiveCalendars, getLoggedUser, (calendars) => {
  return calendars.filter((calendar) => {
    return calendar.class !== "ExternalCalendar";
  });
});

export const getCalendarAssigments = createSelector(
  getAllProjects,
  getTasksByDate,
  getSubTasksByDate,
  getGhostTasksByDate,
  (projects, tasks, subtasks, ghostTasks) => {
    let result = {};

    if (projects) {
      const sortedProjects = projects
        .toArray()
        .reduce((prev, next) => {
          if (!next.is_trashed && !next.is_completed) {
            prev.push({
              id: next.id,
              name: next.name,
              url_path: next.url_path,
              project_number: next.project_number,
              company_id: next.company_id,
            });
          }
          return prev;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));
      if (sortedProjects) {
        result = sortedProjects.reduce((prev, next) => {
          const projectTasks = tasks.filter((task) => task.project_id === next.id).map((task) => task.id);
          const projectSubtasks = subtasks.filter((subtask) => subtask.project_id === next.id).map((task) => task.id);
          const projectGhostTasks = ghostTasks
            .filter((task) => task.project_id === next.id)
            .map((ghostTask) => ghostTask.uuid);

          if (projectTasks.length > 0 || projectGhostTasks.length > 0 || projectSubtasks.length > 0) {
            prev.push({
              ...next,
              tasks: projectTasks,
              ghostTasks: projectGhostTasks,
              subtasks: projectSubtasks,
            });
          }
          return prev;
        }, []);
      }
    }

    return result;
  }
);

const getDateArg = (state, date) => {
  if (moment.isMoment(date)) {
    return date;
  }
  return moment.utc(date);
};

export const getNonWorkingUserIdsByDate = createSelector(
  getAllWeeklyUserCapacityData,
  getNonWorkingDayByDate,
  getTimeWorkdays,
  (state) => state.availability_records,
  getDateArg,
  (capacityData, nonWorkingDays, timeWorkdays, availabilityRecords, date) => {
    let result = [];
    if (nonWorkingDays.length > 0) {
      return result;
    }

    const day = date.day();
    if (timeWorkdays && timeWorkdays.length > 0 && !timeWorkdays.includes(day)) {
      return result;
    }
    const matchedRecords = Object.values(availabilityRecords).filter((record) => {
      return date.unix() >= record.start_date && date.unix() <= record.end_date;
    });

    if (matchedRecords.length > 0) {
      result = matchedRecords.map((r) => r.user_id);
    }

    result = Object.keys(capacityData).reduce((prev, next) => {
      const { capacity = [] } = capacityData[next];
      if (typeof capacity[day] !== "undefined") {
        const value = capacity[day];
        if (value === 0) {
          prev.push(Number(next));
        }
      }
      return prev;
    }, result);
    return [...new Set([...result])];
  }
);

export const getCalendarCountersByDate = createSelector(
  getTasksByDate,
  getSubTasksByDate,
  getGhostTasksByDate,
  getNonWorkingUserIdsByDate,
  getRemindersByDate,
  (tasks, subtasks, ghostTasks, users, reminders) => {
    let result = {
      tasks: tasks.length,
      subtasks: subtasks.length,
      ghostTasks: ghostTasks.length,
      users: users.length,
      reminders: reminders.length,
    };

    return result;
  }
);
